// planificationGeo.js — Phase 2A foundations (Iter95).
//
// GeoJSON (RFC 7946) schema + Turf.js-backed geometry calculations for the
// Planification workspace. Pure, side-effect-free helpers only — no map
// instance, no network, no SOS/alert data of any kind. This module is the
// single source of truth for the Feature shape that Phase 2B (drawing
// tools) and Phase 2C (saved projects) will build on top of; it must not
// change shape without a deliberate migration.
//
// Turf version: 6.5.0, via SCOPED per-function packages (not the
// `@turf/turf` aggregate) — pinned per the Phase 2A spec's explicit
// "prefer stable 6.x" rule. The aggregate package was tried first and
// dropped: `@turf/turf@6.5.0` transitively pulls `@turf/convex`, which
// depends on `concaveman`, a pure-ESM package
// (`import RBush from 'rbush'` with no CJS build) that CRA's default
// Jest config cannot transform (node_modules is excluded from Babel
// transforms by default) — `craco test` failed outright with
// "Cannot use import statement outside a module" the moment this file
// was imported, even though nothing here ever calls convex-hull.
// Importing only the scoped packages we actually use (`@turf/distance`,
// `@turf/length`, `@turf/area`, `@turf/kinks`, `@turf/boolean-point-on-line`
// — the last one added in Phase 2B for tangent-contact detection) avoids
// that dependency chain entirely — verified clean via `yarn why`, zero
// `concaveman`/`rbush` in the tree. Do not switch back to the aggregate
// import without re-verifying this.
import turfDistance from "@turf/distance";
import turfLength from "@turf/length";
import turfArea from "@turf/area";
import turfKinks from "@turf/kinks";
import turfBooleanPointOnLine from "@turf/boolean-point-on-line";

// ---------------------------------------------------------------------
// object_type / purpose contract (spec §5)
// ---------------------------------------------------------------------
export const OBJECT_TYPES = Object.freeze({
  POLYGON: "polygon",
  LINE: "line",
  ANNOTATION: "annotation",
});

export const PURPOSES = Object.freeze({
  DRAWING: "drawing",
  MEASUREMENT: "measurement",
});

// object_type -> GeoJSON geometry.type mapping (spec "règles de correspondance").
const GEOMETRY_TYPE_BY_OBJECT_TYPE = Object.freeze({
  [OBJECT_TYPES.POLYGON]: "Polygon",
  [OBJECT_TYPES.LINE]: "LineString",
  [OBJECT_TYPES.ANNOTATION]: "Point",
});

export const geometryTypeForObjectType = (objectType) => GEOMETRY_TYPE_BY_OBJECT_TYPE[objectType] || null;

// ---------------------------------------------------------------------
// Geometry validation — format (RFC 7946) + topology are both checked;
// these are deliberately separate layers (spec §8 explicitly warns
// against conflating the two).
// ---------------------------------------------------------------------

const isFiniteNumber = (n) => typeof n === "number" && Number.isFinite(n);

/**
 * UUID v4 generator. Prefers the Web Crypto API (`crypto.randomUUID()`,
 * available in every browser this app targets over HTTPS) and falls
 * back to a `Math.random`-seeded RFC 4122 v4 string ONLY when that API
 * is absent — this is specifically the case in the jsdom test
 * environment used by `craco test`, which (unlike real browsers) does
 * not polyfill `crypto.randomUUID`. The fallback is test/dev-safe, not
 * cryptographically strong — fine here since IDs only need to be
 * collision-resistant within one admin's local session, never a
 * security boundary.
 */
const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** RFC 7946 §4: longitude in [-180,180], latitude in [-90,90]. */
export const isValidPosition = (pos) =>
  Array.isArray(pos) &&
  pos.length >= 2 &&
  isFiniteNumber(pos[0]) &&
  isFiniteNumber(pos[1]) &&
  pos[0] >= -180 &&
  pos[0] <= 180 &&
  pos[1] >= -90 &&
  pos[1] <= 90;

/** Point geometry: one valid [lng, lat] position. */
export const isValidPointGeometry = (geometry) =>
  !!geometry && geometry.type === "Point" && isValidPosition(geometry.coordinates);

/** LineString geometry: >=2 valid, non-identical-consecutive positions. */
export const isValidLineGeometry = (geometry) => {
  if (!geometry || geometry.type !== "LineString") return false;
  const coords = geometry.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return false;
  return coords.every(isValidPosition);
};

/** Are two positions numerically identical (RFC 7946 coordinate equality). */
const samePosition = (a, b) => a[0] === b[0] && a[1] === b[1];

// ---------------------------------------------------------------------
// Detailed validation (Phase 2B, per the user's explicit Phase 2A
// follow-up requirement — "ne pas utiliser exclusivement turf.kinks()").
// Each check below has its own named reason CODE (i18n-mapped by the UI,
// see PlanificationTab.jsx's REASON_MESSAGE_KEYS) instead of a single
// opaque boolean, so the user always gets an explicit, specific message.
// ---------------------------------------------------------------------

export const POLYGON_REASON_CODES = Object.freeze({
  NOT_POLYGON_GEOJSON: "NOT_POLYGON_GEOJSON",
  INVALID_COORDINATES: "INVALID_COORDINATES",
  RING_NOT_CLOSED: "RING_NOT_CLOSED",
  TOO_FEW_VERTICES: "TOO_FEW_VERTICES",
  DUPLICATE_CONSECUTIVE_VERTICES: "DUPLICATE_CONSECUTIVE_VERTICES",
  DEGENERATE_RING: "DEGENERATE_RING",
  SELF_INTERSECTING: "SELF_INTERSECTING",
  TANGENT_CONTACT: "TANGENT_CONTACT",
});

export const LINE_REASON_CODES = Object.freeze({
  NOT_LINESTRING_GEOJSON: "NOT_LINESTRING_GEOJSON",
  INVALID_COORDINATES: "INVALID_COORDINATES",
  TOO_FEW_POSITIONS: "TOO_FEW_POSITIONS",
});

/**
 * Detects consecutive (including the closing wrap-around) duplicate
 * vertices in a ring — a zero-length segment that `kinks()` does not
 * treat as a self-intersection (two identical points don't "cross").
 */
const hasDuplicateConsecutiveVertices = (ring) => {
  for (let i = 0; i < ring.length - 1; i += 1) {
    if (samePosition(ring[i], ring[i + 1])) return true;
  }
  return false;
};

/**
 * Tangent-contact detection: a vertex of the ring lying exactly ON a
 * non-adjacent edge (a "T-junction" touch) without the two segments
 * actually crossing through each other. `turf.kinks()` only flags true
 * crossings — a pure touch produces no crossing point, so it passes
 * `kinks()` silently. We check every vertex against every non-adjacent
 * edge with `booleanPointOnLine` (small epsilon tolerance for float
 * rounding) — O(n²) but polygons here are hand-drawn admin shapes
 * (dozens of vertices at most), not imported geometry at scale.
 */
export const hasTangentContact = (geometry) => {
  if (!geometry || geometry.type !== "Polygon") return false;
  const ring = geometry.coordinates[0];
  const n = ring.length - 1; // distinct vertex count (ring is closed)
  for (let i = 0; i < n; i += 1) {
    const vertex = ring[i];
    for (let j = 0; j < n; j += 1) {
      // Skip the 2 edges adjacent to this vertex (edge j->j+1 starting or ending at i).
      if (j === i || j === (i - 1 + n) % n) continue;
      const edge = { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: [ring[j], ring[j + 1]] } };
      try {
        if (turfBooleanPointOnLine(vertex, edge, { epsilon: 1e-9 })) return true;
      } catch (_e) { /* malformed edge — ignored, caught elsewhere by format checks */ }
    }
  }
  return false;
};

/**
 * Self-intersection detection — delegates to Turf's `kinks()`, which walks
 * every pair of non-adjacent ring segments for a geometric intersection
 * (the same segment-pair technique Terra Draw's own built-in polygon
 * validation uses internally, and the one its exported
 * `ValidateNotSelfIntersecting` helper wraps). Using Turf here (rather
 * than depending on Terra Draw internals) keeps this validator usable for
 * geometries that never pass through a live draw session — e.g. a future
 * import/paste path in Phase 2C.
 *
 * Known limitation: `kinks()` alone cannot detect a vertex that merely
 * *touches* another edge without crossing it — that case is covered
 * separately by `hasTangentContact` above (Phase 2B requirement).
 */
export const hasSelfIntersection = (geometry) => {
  if (!geometry || geometry.type !== "Polygon") return false;
  try {
    const result = turfKinks({ type: "Feature", properties: {}, geometry });
    return result.features.length > 0;
  } catch (_e) {
    // Malformed input Turf can't even parse — treat as "invalid", not "no intersection".
    return true;
  }
};

/**
 * Full Polygon validation with a specific reason code on failure. Order
 * matters — cheapest/most-specific checks first so the user gets the
 * most actionable message (e.g. "ring not closed" rather than a generic
 * "self-intersecting" false positive caused by the same root cause).
 * Deliberately does NOT check whether the geometry lies inside Mali —
 * the country mask is a visual layer only (spec §9), never a geometry
 * acceptance rule.
 */
export const validatePolygonGeometryDetailed = (geometry) => {
  if (!geometry || geometry.type !== "Polygon") return { valid: false, reason: POLYGON_REASON_CODES.NOT_POLYGON_GEOJSON };
  const rings = geometry.coordinates;
  if (!Array.isArray(rings) || rings.length !== 1) return { valid: false, reason: POLYGON_REASON_CODES.NOT_POLYGON_GEOJSON };
  const ring = rings[0];
  if (!Array.isArray(ring) || ring.length < 4) return { valid: false, reason: POLYGON_REASON_CODES.TOO_FEW_VERTICES };
  if (!ring.every(isValidPosition)) return { valid: false, reason: POLYGON_REASON_CODES.INVALID_COORDINATES };
  if (!samePosition(ring[0], ring[ring.length - 1])) return { valid: false, reason: POLYGON_REASON_CODES.RING_NOT_CLOSED };
  if (hasDuplicateConsecutiveVertices(ring)) return { valid: false, reason: POLYGON_REASON_CODES.DUPLICATE_CONSECUTIVE_VERTICES };

  const distinct = ring.slice(0, -1);
  const uniqueCount = new Set(distinct.map((p) => `${p[0]},${p[1]}`)).size;
  if (uniqueCount < 3) return { valid: false, reason: POLYGON_REASON_CODES.TOO_FEW_VERTICES };

  let area;
  try {
    area = turfArea({ type: "Feature", properties: {}, geometry });
  } catch (_e) {
    return { valid: false, reason: POLYGON_REASON_CODES.DEGENERATE_RING };
  }
  if (!(area > 0)) return { valid: false, reason: POLYGON_REASON_CODES.DEGENERATE_RING }; // collinear ring -> zero area

  if (hasTangentContact(geometry)) return { valid: false, reason: POLYGON_REASON_CODES.TANGENT_CONTACT };
  if (hasSelfIntersection(geometry)) return { valid: false, reason: POLYGON_REASON_CODES.SELF_INTERSECTING };

  return { valid: true, reason: null };
};

/** Full LineString validation with a reason code — see §5.2 ("empêcher la
 *  validation d'une ligne comportant moins de deux positions valides"). */
export const validateLineGeometryDetailed = (geometry) => {
  if (!geometry || geometry.type !== "LineString") return { valid: false, reason: LINE_REASON_CODES.NOT_LINESTRING_GEOJSON };
  const coords = geometry.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return { valid: false, reason: LINE_REASON_CODES.TOO_FEW_POSITIONS };
  if (!coords.every(isValidPosition)) return { valid: false, reason: LINE_REASON_CODES.INVALID_COORDINATES };
  return { valid: true, reason: null };
};

/** Boolean convenience wrapper — kept for Phase 2A call sites/tests. */
export const isValidPolygonGeometry = (geometry) => validatePolygonGeometryDetailed(geometry).valid;

export const validateGeometry = (geometry) => {
  if (!geometry || typeof geometry.type !== "string") return false;
  if (geometry.type === "Point") return isValidPointGeometry(geometry);
  if (geometry.type === "LineString") return isValidLineGeometry(geometry);
  if (geometry.type === "Polygon") return isValidPolygonGeometry(geometry);
  return false; // MultiPoint/MultiLineString/MultiPolygon/GeometryCollection: out of scope
};

// ---------------------------------------------------------------------
// Feature contract (spec §5) + FeatureCollection ops (spec §6)
// ---------------------------------------------------------------------

const REQUIRED_STRING_PROPS = ["name", "description", "color"];

/**
 * Validates a Feature against the Phase 2A contract: required top-level
 * keys, required `properties.*` keys/types, geometry<->object_type
 * correspondence, and geometry validity itself.
 * Returns `{ valid: boolean, errors: string[] }` — never throws.
 */
export const validateFeature = (feature) => {
  const errors = [];
  if (!feature || feature.type !== "Feature") errors.push("type must be 'Feature'");
  if (!feature?.id || typeof feature.id !== "string") errors.push("id must be a non-empty string (UUID)");
  if (!validateGeometry(feature?.geometry)) errors.push("geometry is missing or invalid");

  const props = feature?.properties || {};
  if (!Object.values(OBJECT_TYPES).includes(props.object_type)) errors.push("properties.object_type is invalid");
  if (!Object.values(PURPOSES).includes(props.purpose)) errors.push("properties.purpose is invalid");
  REQUIRED_STRING_PROPS.forEach((key) => {
    if (typeof props[key] !== "string") errors.push(`properties.${key} must be a string`);
  });
  if (typeof props.opacity !== "number" || props.opacity < 0 || props.opacity > 1) {
    errors.push("properties.opacity must be a number between 0 and 1");
  }
  if (typeof props.created_at !== "string" || Number.isNaN(Date.parse(props.created_at))) {
    errors.push("properties.created_at must be an ISO 8601 date string");
  }

  if (!errors.length && feature.geometry && props.object_type) {
    const expected = geometryTypeForObjectType(props.object_type);
    if (expected && feature.geometry.type !== expected) {
      errors.push(`geometry.type '${feature.geometry.type}' does not match object_type '${props.object_type}' (expected '${expected}')`);
    }
  }
  return { valid: errors.length === 0, errors };
};

/**
 * Builds a spec-compliant Feature. Generates a stable UUID (v4, via the
 * Web Crypto API already available in every supported browser + Node 20+
 * test runner — no extra `uuid` dependency needed).
 * Throws if the resulting Feature fails `validateFeature` — callers (the
 * future drawing tools) are expected to catch this at the UI boundary.
 */
export const createFeature = ({ id, objectType, purpose, geometry, name = "", description = "", color = "#00B894", opacity = 0.4 }) => {
  const feature = {
    type: "Feature",
    id: id || generateUUID(),
    geometry,
    properties: {
      object_type: objectType,
      purpose,
      name,
      description,
      color,
      opacity,
      created_at: new Date().toISOString(),
    },
  };
  const { valid, errors } = validateFeature(feature);
  if (!valid) throw new Error(`createFeature: invalid feature — ${errors.join("; ")}`);
  return feature;
};

export const createEmptyFeatureCollection = () => ({ type: "FeatureCollection", features: [] });

/** Immutable insert — rejects duplicate ids (UUID uniqueness, spec §6). */
export const addFeatureToCollection = (collection, feature) => {
  if (collection.features.some((f) => f.id === feature.id)) {
    throw new Error(`addFeatureToCollection: duplicate id ${feature.id}`);
  }
  return { ...collection, features: [...collection.features, feature] };
};

/**
 * Immutable property update — geometry and id are always preserved
 * verbatim (spec §5 "Préserver les coordonnées lors des modifications de
 * propriétés" / "Préserver les UUID lors des modifications"); only
 * `properties.*` keys present in `propertyUpdates` are merged in.
 */
export const updateFeatureInCollection = (collection, id, propertyUpdates) => ({
  ...collection,
  features: collection.features.map((f) =>
    f.id === id ? { ...f, properties: { ...f.properties, ...propertyUpdates } } : f
  ),
});

export const removeFeatureFromCollection = (collection, id) => ({
  ...collection,
  features: collection.features.filter((f) => f.id !== id),
});

/**
 * Immutable GEOMETRY update (Phase 2B — vertex move/add/delete via Terra
 * Draw's select mode). Unlike `updateFeatureInCollection` (properties
 * only), this is the counterpart for edits that legitimately change
 * coordinates while still preserving `id` and all other properties
 * verbatim ("aucune modification de coordonnées lors d'un changement de
 * propriété" cuts the other way too: a geometry edit must never touch
 * unrelated properties).
 */
export const updateFeatureGeometryInCollection = (collection, id, newGeometry) => ({
  ...collection,
  features: collection.features.map((f) => (f.id === id ? { ...f, geometry: newGeometry } : f)),
});

/** Replaces a feature with a FULL prior snapshot verbatim (id/geometry/
 *  properties all overwritten) — used by undo/redo to restore an exact
 *  `before`/`after` snapshot regardless of whether the original action
 *  touched properties, geometry, or both. */
export const replaceFeatureInCollection = (collection, snapshotFeature) => ({
  ...collection,
  features: collection.features.map((f) => (f.id === snapshotFeature.id ? snapshotFeature : f)),
});

export const getFeatureById = (collection, id) => collection.features.find((f) => f.id === id) || null;

// ---------------------------------------------------------------------
// Turf-backed measurement helpers — zoom/pixel/map-mode independent,
// operate purely on GeoJSON geometry. No formula is reimplemented by
// hand; everything below delegates to Turf.
// ---------------------------------------------------------------------

/** Great-circle distance between two [lng, lat] positions, in meters. */
export const distanceMeters = (posA, posB) => {
  if (!isValidPosition(posA) || !isValidPosition(posB)) throw new Error("distanceMeters: invalid position");
  return turfDistance(posA, posB, { units: "kilometers" }) * 1000;
};

/** Total length of a LineString geometry/Feature, in meters. */
export const lineLengthMeters = (lineGeometryOrFeature) => {
  const geometry = lineGeometryOrFeature.type === "Feature" ? lineGeometryOrFeature.geometry : lineGeometryOrFeature;
  if (!isValidLineGeometry(geometry)) throw new Error("lineLengthMeters: invalid LineString geometry");
  return turfLength({ type: "Feature", properties: {}, geometry }, { units: "kilometers" }) * 1000;
};

/** LIVE preview length (Phase 2B) for a line still being drawn — same
 *  "never throw while drawing" contract as `previewPolygonAreaSqMeters`. */
export const previewLineLengthMeters = (coordinates) => {
  const valid = Array.isArray(coordinates) ? coordinates.filter(isValidPosition) : [];
  if (valid.length < 2) return 0;
  try {
    return lineLengthMeters({ type: "LineString", coordinates: valid });
  } catch (_e) {
    return 0;
  }
};

/** Area of a Polygon geometry/Feature, in square meters. */
export const polygonAreaSqMeters = (polygonGeometryOrFeature) => {
  const geometry = polygonGeometryOrFeature.type === "Feature" ? polygonGeometryOrFeature.geometry : polygonGeometryOrFeature;
  if (!isValidPolygonGeometry(geometry)) throw new Error("polygonAreaSqMeters: invalid Polygon geometry");
  return turfArea({ type: "Feature", properties: {}, geometry });
};

/**
 * LIVE preview area (Phase 2B) for a polygon still being drawn — the
 * ring is virtually closed (first coordinate appended if the caller
 * didn't already close it) purely for this informational readout; the
 * real `polygonAreaSqMeters`/strict validator above is what actually
 * gates acceptance into the FeatureCollection once the user finishes.
 * Returns 0 for <3 distinct positions (nothing to preview yet) instead
 * of throwing — this is read on every pointer move while drawing.
 */
export const previewPolygonAreaSqMeters = (ringCoordinates) => {
  if (!Array.isArray(ringCoordinates) || ringCoordinates.length < 3) return 0;
  const valid = ringCoordinates.filter(isValidPosition);
  if (valid.length < 3) return 0;
  const closed = samePosition(valid[0], valid[valid.length - 1]) ? valid : [...valid, valid[0]];
  try {
    return Math.abs(turfArea({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [closed] } }));
  } catch (_e) {
    return 0;
  }
};

// ---------------------------------------------------------------------
// Unit conversions + display formatting
// ---------------------------------------------------------------------

export const metersToKm = (m) => m / 1000;
export const sqMetersToHectares = (sqm) => sqm / 10000;
export const sqMetersToSqKm = (sqm) => sqm / 1_000_000;

/** "842 m" below 1 km, "3.42 km" at/above 1 km. */
export const formatDistance = (meters) =>
  meters >= 1000 ? `${metersToKm(meters).toFixed(2)} km` : `${Math.round(meters)} m`;

/** "0.42 ha" below 1 km², "3.10 km²" at/above 1 km². */
export const formatArea = (sqMeters) =>
  sqMeters >= 1_000_000 ? `${sqMetersToSqKm(sqMeters).toFixed(2)} km²` : `${sqMetersToHectares(sqMeters).toFixed(2)} ha`;
